import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import openNavigation from '../../../util/openNavigation';
import PaymentCollectionScreen from './PaymentCollectionScreen';

// Bottom-sheet shown after a tech taps "Complete Service" on the active stop.
//
// Two variants by request type:
//
// • RETAIL — gated. Tech MUST collect payment via PaymentCollectionScreen
//   before Navigate to Next Stop unlocks. Collection options: Pay now (open
//   hosted page on this device) / Send pay link (SMS+email) / Cash / Check.
//   See docs/MOBILE_SERVICE_MODE_PLAN.md Phase J and project_mobile_payment_collection.
//
// • DEALER — no in-field collection. Invoice was auto-created on Complete
//   Service and routes via the dealer's existing billing cycle. Sheet shows
//   the invoice confirmation banner and immediately enables Navigate.
//
// Props:
//   show              — boolean
//   onHide()          — close handler
//   nextStop          — next stop object, or null if last of day
//   baseAddress       — { address, lat, lng } of the location's base
//   onNavigateNext()  — caller hook fires `started_travel_at` PATCH for nextStop
//   invoice           — { invoiceId, paymentId, amount, draft } from createInvoice;
//                       null if invoice creation failed (Navigate stays open
//                       with a soft warning rather than blocking the route).
//   requestType       — 'retail' | 'dealer' — drives the variant
//   requestId         — the just-finished request id (for PaymentCollectionScreen)
//   installerId       — tech / installer id (for PaymentCollectionScreen)
//   customerName      — customer.name from the stop
//   vehicleLabel      — vehicle_label from the stop

const formatPreview = (stop) => {
  if (!stop) return '';
  const name = stop.customer?.name || 'Next customer';
  const distance = stop.travel_distance_miles != null
    ? `${Number(stop.travel_distance_miles).toFixed(stop.travel_distance_miles < 10 ? 1 : 0)} mi`
    : null;
  const minutes = stop.travel_minutes_in != null
    ? `${Math.round(stop.travel_minutes_in)} min`
    : null;
  const arriveAt = stop.start_time ? `arrive ~${stop.start_time}` : null;
  const meta = [distance, minutes, arriveAt].filter(Boolean).join(' · ');
  return meta ? `${name} · ${meta}` : name;
};

const PostFinishSheet = ({
  show,
  onHide,
  nextStop,
  baseAddress,
  onNavigateNext,
  invoice,
  requestType,
  requestId,
  installerId,
  customerName,
  vehicleLabel,
}) => {
  const isLastStop = !nextStop;
  const hasBase = !!(baseAddress && (baseAddress.address || (baseAddress.lat && baseAddress.lng)));
  const isRetail = (requestType || 'retail') === 'retail';
  const hasInvoice = !!invoice;

  const [showCollection, setShowCollection] = useState(false);
  const [collectionResult, setCollectionResult] = useState(null);

  // Reset internal state whenever the sheet is dismissed so the next stop
  // doesn't inherit a stale "paid" banner.
  useEffect(() => {
    if (!show) {
      setShowCollection(false);
      setCollectionResult(null);
    }
  }, [show]);

  // Retail collection gate: Navigate unlocks once one of the 4 chips fires.
  // Dealer: no gate — Navigate is always unlocked (invoice auto-routes to dealer).
  // Retail without invoice (creation failed): also unlock Navigate with a soft
  // warning so the tech isn't stranded.
  const collectionRequired = isRetail && hasInvoice && !collectionResult;

  const handlePrimary = () => {
    if (!isLastStop) {
      if (onNavigateNext) onNavigateNext(nextStop);
      else openNavigation(nextStop.customer || {});
    } else if (hasBase) {
      openNavigation(baseAddress);
    }
    onHide && onHide();
  };

  const handleCollected = (result) => {
    setCollectionResult(result);
    setShowCollection(false);
  };

  return (
    <>
      <Modal show={show && !showCollection} onHide={onHide} centered>
        <Modal.Body className="text-center py-4">
          <i
            className="fa-sharp fa-regular fa-circle-check"
            aria-hidden="true"
            style={{ fontSize: 48, color: 'var(--color-success)', marginBottom: 12 }}
          />
          <h4 className="mb-3">Stop finished</h4>

          {/* Dealer invoice confirmation banner */}
          {!isRetail && hasInvoice && (
            <div
              className="alert alert-success py-2 mb-3 text-left"
              style={{ fontSize: 13 }}
            >
              <i className="fa-sharp fa-regular fa-file-invoice mr-2" aria-hidden="true" />
              Invoice {invoice.invoiceId ? `#${invoice.invoiceId} ` : ''}sent for dealer billing
            </div>
          )}

          {/* Retail collection result banner (after a chip fires) */}
          {isRetail && collectionResult && (
            <div
              className="alert alert-success py-2 mb-3 text-left"
              style={{ fontSize: 13 }}
            >
              <i className="fa-sharp fa-regular fa-circle-check mr-2" aria-hidden="true" />
              {collectionResult.method === 'already_paid' && 'Customer already paid online.'}
              {collectionResult.method === 'card_now' && 'Pay page opened — Stripe will confirm payment.'}
              {collectionResult.method === 'card_link' && 'Pay link sent to customer.'}
              {collectionResult.method === 'cash' && 'Marked paid (cash).'}
              {collectionResult.method === 'check' && (
                collectionResult.checkNumber
                  ? `Marked paid (check #${collectionResult.checkNumber}).`
                  : 'Marked paid (check).'
              )}
            </div>
          )}

          {/* Retail soft-warning when invoice creation failed upstream */}
          {isRetail && !hasInvoice && (
            <div
              className="alert alert-warning py-2 mb-3 text-left"
              style={{ fontSize: 13 }}
            >
              <i className="fa-sharp fa-regular fa-triangle-exclamation mr-2" aria-hidden="true" />
              Invoice didn't generate. Collect payment from the request panel later.
            </div>
          )}

          {/* Retail Collect Payment CTA — primary when uncollected */}
          {collectionRequired && (
            <Button
              variant="success"
              size="lg"
              block
              className="mb-2"
              onClick={() => setShowCollection(true)}
            >
              <i className="fa-sharp fa-regular fa-credit-card mr-2" aria-hidden="true" />
              Collect Payment {invoice.amount != null ? `· $${(Number(invoice.amount) / 100).toFixed(2)}` : ''}
            </Button>
          )}

          {!isLastStop && (
            <>
              <div className="text-muted mb-3" style={{ fontSize: 14 }}>
                Next stop in {nextStop.travel_minutes_in != null ? `${Math.round(nextStop.travel_minutes_in)} min` : 'a moment'}
              </div>
              <div className="mb-3" style={{ fontSize: 15 }}>{formatPreview(nextStop)}</div>
              <Button
                variant={collectionRequired ? 'outline-secondary' : 'primary'}
                size="lg"
                block
                onClick={handlePrimary}
                disabled={collectionRequired}
              >
                <i className="fa-sharp fa-regular fa-diamond-turn-right mr-2" aria-hidden="true" />
                Navigate to Next Stop
              </Button>
              {collectionRequired && (
                <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                  Collect payment to continue
                </div>
              )}
            </>
          )}

          {isLastStop && hasBase && (
            <>
              <div className="mb-3 text-muted">Last stop of the day.</div>
              <Button
                variant={collectionRequired ? 'outline-secondary' : 'primary'}
                size="lg"
                block
                onClick={handlePrimary}
                disabled={collectionRequired}
              >
                <i className="fa-sharp fa-regular fa-house mr-2" aria-hidden="true" />
                Navigate to Base
              </Button>
              {collectionRequired && (
                <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                  Collect payment to continue
                </div>
              )}
            </>
          )}

          {isLastStop && !hasBase && (
            <>
              <div className="mb-3 text-muted">Day complete.</div>
              <Button
                variant={collectionRequired ? 'outline-secondary' : 'success'}
                size="lg"
                block
                onClick={onHide}
                disabled={collectionRequired}
              >
                <i className="fa-sharp fa-regular fa-flag-checkered mr-2" aria-hidden="true" />
                Done for the day
              </Button>
              {collectionRequired && (
                <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                  Collect payment to continue
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Mandatory retail collection modal — stacks on top of the post-finish
          sheet. PostFinishSheet is hidden (show={false}) while it's open so
          the two modals don't fight over backdrop layering. */}
      <PaymentCollectionScreen
        show={showCollection && isRetail && hasInvoice}
        invoice={invoice}
        requestId={requestId}
        installerId={installerId}
        customerName={customerName}
        vehicleLabel={vehicleLabel}
        onCollected={handleCollected}
      />
    </>
  );
};

export default PostFinishSheet;
